/**
 * ビジネス向けROIシミュレーター
 * ================================================================================
 * 3つの事業用ビジネスモデル + 1つの家庭用モデルのROI計算ロジック
 *
 * 【計算タイプ】
 * - ROI計算: 通常のROI・回収期間を算出
 * - 初期費用逆算: 目標回収期間から許容できる初期投資上限を逆算
 * - 単価アップ逆算: 目標回収期間から必要な単価アップ率を逆算
 */

// ================================================================================
// モデル設定
// ================================================================================
const modelConfigs = {
  hotel_ryokan: {
    tab: 'business',
    name: '旅館・ホテル(客室単価アップ)',
    badgeText: '旅館・ホテル向け',
    inputCardTitle: 'お客様の施設情報',
    sectionTitles: ['施設の基本情報', '規模と値上げ設定'],
    hideUsage: true,
    labels: {
      price: '客室単価',
      occupancy: '稼働率',
      usage: '(非表示)',
      units: '客室数',
      uplift: '単価アップ率'
    },
    helpTexts: {
      price: '(現在の1泊あたり単価)',
      occupancy: '(年間の客室稼働割合)',
      usage: '',
      uplift: '(サウナ導入後の値上げ幅)'
    },
    unitSuffix: '室',
    sliders: {
      price: { min: 5000, max: 80000, val: 15000, step: 1000 },
      occupancy: { min: 30, max: 100, val: 65, step: 1 },
      usage: { min: 5, max: 100, val: 70, step: 1 },
      units: { min: 1, max: 100, val: 5, step: 1 },
      uplift: { min: 1, max: 50, val: 15, step: 1 }
    },
    // 各部屋にサウナ設置: 130万/台 + 送料30万固定 → 5台: 680万
    costs: { initial: 6800000, running: 500000 },
    periodLabel: '365日',
    periodMultiplier: 365,
    // 各部屋にサウナ設置→全室値上げ対象のため利用率不要
    calc: (p) => p.price * (p.uplift / 100) * (p.occupancy / 100) * p.units * 365,
    // プリセットは台数と導入費用のみ変更（基本情報はそのまま）
    presets: {
      small:  { label: '3室',  units: 3,  cost: 4200000 },
      medium: { label: '10室', units: 10, cost: 13300000 },
      large:  { label: '25室', units: 25, cost: 32800000 }
    }
  },
  housing_rental: {
    tab: 'business',
    name: '賃貸マンション（家賃アップ）',
    badgeText: '賃貸マンション向け',
    inputCardTitle: 'お客様の物件情報',
    sectionTitles: ['物件の基本情報', '規模と家賃設定'],
    hideUsage: true,
    labels: {
      price: '月額家賃',
      occupancy: '入居率',
      usage: '(非表示)',
      units: '戸数',
      uplift: '家賃アップ率'
    },
    helpTexts: {
      price: '(1戸あたりの月額家賃)',
      occupancy: '(年間の平均入居率)',
      usage: '',
      uplift: '(サウナ導入後の家賃値上げ幅)'
    },
    unitSuffix: '戸',
    sliders: {
      price: { min: 30000, max: 300000, val: 80000, step: 5000 },
      occupancy: { min: 50, max: 100, val: 95, step: 1 },
      usage: { min: 100, max: 100, val: 100, step: 1 },
      units: { min: 1, max: 100, val: 20, step: 1 },
      uplift: { min: 1, max: 30, val: 15, step: 1 }
    },
    // 各部屋にサウナ設置: 130万×20戸 + 送料30万 = 2630万
    costs: { initial: 26300000, running: 500000 },
    periodLabel: '12ヶ月',
    periodMultiplier: 12,
    calc: (p) => p.price * (p.uplift / 100) * (p.occupancy / 100) * p.units * 12,
    presets: {
      small:  { label: '10戸', units: 10, cost: 13300000 },
      medium: { label: '30戸', units: 30, cost: 39300000 },
      large:  { label: '50戸', units: 50, cost: 65300000 }
    }
  },
  home_consumer: {
    tab: 'consumer',
    name: '家庭用サウナ vs 施設(損益分岐点)',
    badgeText: '家庭用サウナ',
    inputCardTitle: '現在のサウナ利用状況',
    sectionTitles: ['サウナ施設の利用状況', '利用頻度'],
    labels: {
      price: '施設代/回',
      occupancy: '交通費/回',
      usage: '週の頻度',
      units: '(非表示)',
      uplift: '(非表示)'
    },
    helpTexts: {
      price: '(1回あたりのサウナ施設利用料)',
      occupancy: '(1回あたりの往復交通費)',
      usage: '(週に何回サウナに行くか)',
      uplift: ''
    },
    unitSuffix: '回',
    sliders: {
      price: { min: 500, max: 3000, val: 1000, step: 100 },
      occupancy: { min: 0, max: 1000, val: 500, step: 50 },
      usage: { min: 1, max: 7, val: 3, step: 1 },
      units: { min: 52, max: 52, val: 52, step: 1 },
      uplift: { min: 0, max: 0, val: 0, step: 1 }
    },
    costs: { initial: 1500000, running: 35000 },
    periodLabel: '52週',
    periodMultiplier: 52,
    calc: (p) => (p.price + p.occupancy) * p.usage * 52,
    // 家庭用はクイック設定なし
    presets: null
  },
  health_spa: {
    tab: 'business',
    name: '温浴施設（集客アップ）',
    badgeText: '温浴施設向け',
    inputCardTitle: 'お客様の施設情報',
    sectionTitles: ['施設の基本情報', '規模と集客設定'],
    hideUsage: true,
    showBathingTax: true, // 入湯税入力を表示
    labels: {
      price: '客単価',
      occupancy: '1日来場者数',
      usage: '(非表示)',
      units: '営業日数',
      uplift: '客単価アップ率'
    },
    helpTexts: {
      price: '(サウナ導入前の1人あたり利用料金)',
      occupancy: '(1日あたりの平均来場者数)',
      usage: '',
      uplift: '(サウナ導入による値上げ率)'
    },
    unitSuffix: '日',
    sliders: {
      price: { min: 500, max: 5000, val: 800, step: 100 },
      occupancy: { min: 10, max: 1000, val: 100, step: 10 },
      usage: { min: 100, max: 100, val: 100, step: 1 },
      units: { min: 200, max: 365, val: 340, step: 5 },
      uplift: { min: 0, max: 50, val: 25, step: 1 }
    },
    // デフォルト: 5人用サウナ、来場者100人/日、客単価800円、アップ率25%
    costs: { initial: 4000000, running: 1000000 },
    defaultCapacity: 5,
    defaultBathingTax: 150,
    periodLabel: null,
    periodMultiplier: null,
    // (客単価 × アップ率 - 入湯税) × 来場者数 × 営業日数
    calc: (p) => {
      const bathingTax = parseNumber(document.getElementById('input-bathing-tax')?.value || '150');
      const perVisitorIncrease = p.price * (p.uplift / 100) - bathingTax;
      return Math.max(0, perVisitorIncrease) * p.occupancy * p.units;
    },
    // プリセット: 収容人数・来場者数・導入費用を変更
    // 大型サウナは大規模施設に導入されるため来場者も多い
    presets: {
      small:  { label: '5人用',  capacity: 5,  occupancy: 100, cost: 4000000 },
      medium: { label: '10人用', capacity: 10, occupancy: 200, cost: 6000000 },
      large:  { label: '20人用', capacity: 20, occupancy: 400, cost: 10000000 }
    }
  }
};

let currentModel = 'hotel_ryokan';
let currentCalculationType = 'roi';
let spaCapacity = 5; // 温浴施設のサウナ収容人数（プリセットで変更）

// ================================================================================
// タブ切り替え
// ================================================================================
function switchTab(el) {
  document.querySelectorAll('.roi-tab').forEach(t => t.classList.remove('active'));
  el.classList.add('active');
  const tab = el.dataset.tab;
  const isConsumer = tab === 'consumer';

  // 家庭用ではモデルバー全体を非表示（業種選択・計算タイプ不要）
  const modelBar = document.querySelector('.roi-model-bar');
  if (modelBar) modelBar.style.display = isConsumer ? 'none' : '';

  if (isConsumer) {
    currentCalculationType = 'roi';
    const calcTypeSelect = document.getElementById('calculationTypeSelect');
    if (calcTypeSelect) calcTypeSelect.value = 'roi';
  }

  const industrySelect = document.getElementById('industrySelect');
  if (isConsumer) {
    currentModel = 'home_consumer';
  } else {
    currentModel = 'hotel_ryokan';
    industrySelect.value = 'hotel_ryokan';
  }
  onIndustryChange();
}

// ================================================================================
// 業種変更
// ================================================================================
function onIndustryChange() {
  const key = currentModel === 'home_consumer' ? 'home_consumer' : document.getElementById('industrySelect').value;
  currentModel = key;
  const cfg = modelConfigs[key];

  const isConsumer = key === 'home_consumer';
  document.querySelectorAll('.roi-tab').forEach(t => {
    t.classList.toggle('active', t.dataset.tab === (isConsumer ? 'consumer' : 'business'));
  });

  // 家庭用ではモデルバー全体を非表示
  const modelBar = document.querySelector('.roi-model-bar');
  if (modelBar) modelBar.style.display = isConsumer ? 'none' : '';

  // バッジ更新
  const badge = document.getElementById('roi-header-badge');
  if (badge) badge.textContent = cfg.badgeText;

  // 入力カードタイトル更新
  const inputTitle = document.getElementById('input-card-title');
  if (inputTitle) inputTitle.textContent = cfg.inputCardTitle;

  // セクションタイトル更新
  if (cfg.sectionTitles) {
    const s1 = document.getElementById('section-title-1');
    const s2 = document.getElementById('section-title-2');
    if (s1) s1.textContent = cfg.sectionTitles[0];
    if (s2) s2.textContent = cfg.sectionTitles[1];
  }

  // ラベル更新
  Object.keys(cfg.labels).forEach(k => {
    const labelEl = document.getElementById('label-' + k);
    if (labelEl) labelEl.textContent = cfg.labels[k];
  });

  if (cfg.helpTexts) {
    Object.keys(cfg.helpTexts).forEach(k => {
      const helpEl = document.getElementById('help-' + k);
      if (helpEl) helpEl.textContent = cfg.helpTexts[k];
    });
  }

  // スライダー更新
  Object.keys(cfg.sliders).forEach(k => {
    const slider = document.getElementById('slider-' + k);
    if (slider) {
      const s = cfg.sliders[k];
      slider.min = s.min;
      slider.max = s.max;
      slider.value = s.val;
      slider.step = s.step;
      const minEl = document.getElementById('min-' + k);
      const maxEl = document.getElementById('max-' + k);
      if (minEl) minEl.textContent = formatSliderRangeLabel(k, s.min, cfg);
      if (maxEl) maxEl.textContent = formatSliderRangeLabel(k, s.max, cfg);
    }
  });

  // コスト初期値
  document.getElementById('input-cost').value = formatNumber(cfg.costs.initial);
  document.getElementById('input-running').value = formatNumber(cfg.costs.running);

  // 温浴施設のデフォルト収容人数をリセット
  if (cfg.defaultCapacity) spaCapacity = cfg.defaultCapacity;

  // プリセットバーの表示/非表示 + ラベル更新
  const presetBar = document.getElementById('preset-bar');
  if (presetBar) presetBar.style.display = cfg.presets ? 'flex' : 'none';
  updatePresetLabels();

  updateInputVisibility();
  calculate();
}

// ================================================================================
// プリセット機能
// ================================================================================
function applyPreset(size) {
  const cfg = modelConfigs[currentModel];
  if (!cfg.presets || !cfg.presets[size]) return;
  const p = cfg.presets[size];

  // 台数/室数/戸数
  if (p.units !== undefined) {
    const unitsEl = document.getElementById('slider-units');
    if (unitsEl) unitsEl.value = p.units;
  }

  // 温浴施設: 収容人数を保存
  if (p.capacity !== undefined) {
    spaCapacity = p.capacity;
  }

  // 温浴施設: 来場者数も変更（施設規模に連動）
  if (p.occupancy !== undefined) {
    const occEl = document.getElementById('slider-occupancy');
    if (occEl) occEl.value = p.occupancy;
  }

  // 導入費用
  document.getElementById('input-cost').value = formatNumber(p.cost);

  calculate();
}

function updatePresetLabels() {
  const cfg = modelConfigs[currentModel];
  if (!cfg.presets) return;
  ['small', 'medium', 'large'].forEach(size => {
    const el = document.getElementById('preset-' + size + '-label');
    if (el && cfg.presets[size]) el.textContent = cfg.presets[size].label;
  });
}

// ================================================================================
// 計算タイプ変更
// ================================================================================
function onCalculationTypeChange() {
  currentCalculationType = document.getElementById('calculationTypeSelect').value;
  resetDisplayMode();
  calculate();
}

// ================================================================================
// 表示モードリセット
// ================================================================================
function resetDisplayMode() {
  const paybackTitle = document.querySelector('.roi-kpi-section:has(#payback-years) .roi-kpi-title');
  if (paybackTitle) paybackTitle.textContent = '投資回収期間';

  const paybackEl = document.getElementById('payback-years');
  if (paybackEl) paybackEl.style.fontSize = '48px';
  const paybackMonthsNumEl = document.getElementById('payback-months-num');
  if (paybackMonthsNumEl) paybackMonthsNumEl.style.display = 'inline';
  const paybackUnits = document.querySelectorAll('.roi-payback-unit');
  paybackUnits.forEach((u, i) => {
    u.style.display = 'inline';
    u.textContent = i === 0 ? '年' : 'ヶ月';
  });

  const roiSection = document.querySelector('.roi-kpi-section:has(#gauge-arc)');
  const profitSection = document.querySelector('.roi-kpi-section:has(#chart-container)');
  const isConsumer = currentModel === 'home_consumer';

  if (roiSection) roiSection.style.display = isConsumer ? 'none' : 'block';
  if (profitSection) profitSection.style.display = isConsumer ? 'none' : 'block';

  const warningEl = document.getElementById('negative-warning');
  if (warningEl) warningEl.classList.remove('show');

  updateInputVisibility();
}

// ================================================================================
// 入力欄の表示/非表示制御
// ================================================================================
function updateInputVisibility() {
  const type = currentCalculationType;
  const cfg = modelConfigs[currentModel];
  const isConsumer = currentModel === 'home_consumer';

  const costSection = document.getElementById('cost-section');
  const upliftGroup = document.getElementById('slider-uplift')?.closest('.roi-input-group');
  const targetPaybackGroup = document.getElementById('target-payback-group');
  const targetPaybackHelp = document.getElementById('target-payback-help');
  const unitsGroup = document.getElementById('slider-units')?.closest('.roi-input-group');
  const usageGroup = document.getElementById('slider-usage')?.closest('.roi-input-group');

  if (unitsGroup) unitsGroup.style.display = isConsumer ? 'none' : 'block';
  if (usageGroup) usageGroup.style.display = cfg.hideUsage ? 'none' : 'block';

  // 入湯税（温浴施設のみ）
  const bathingTaxGroup = document.getElementById('bathing-tax-group');
  if (bathingTaxGroup) bathingTaxGroup.style.display = cfg.showBathingTax ? 'block' : 'none';

  if (type === 'roi') {
    if (costSection) costSection.style.display = '';
    if (upliftGroup) upliftGroup.style.display = isConsumer ? 'none' : 'block';
    if (targetPaybackGroup) targetPaybackGroup.style.display = 'none';
  } else if (type === 'budget_reverse') {
    // 初期費用逆算: 導入費用セクション全体を非表示
    if (costSection) costSection.style.display = 'none';
    if (upliftGroup) upliftGroup.style.display = 'block';
    if (targetPaybackGroup) targetPaybackGroup.style.display = 'block';
    if (targetPaybackHelp) targetPaybackHelp.textContent = '※ この期間内で初期費用を回収するために、かけられる上限額を算出します。';
  } else if (type === 'uplift_reverse') {
    if (costSection) costSection.style.display = '';
    if (upliftGroup) upliftGroup.style.display = 'none';
    if (targetPaybackGroup) targetPaybackGroup.style.display = 'block';
    if (targetPaybackHelp) targetPaybackHelp.textContent = '※ この期間内で初期費用を回収するために、必要な単価アップ率を算出します。';
  }
}

// ================================================================================
// フォーマット関数
// ================================================================================
function formatSliderRangeLabel(key, value, cfg) {
  if (key === 'price') return formatYen(value);
  const label = cfg.labels[key] || '';
  if (label.includes('交通費')) return formatYen(value);
  if (label.includes('来場者') || label.includes('人数')) return value + '人';
  if (label.includes('日数') || label.includes('営業日')) return value + '日';
  if (label.includes('月数')) return value + 'ヶ月';
  if (label.includes('週の頻度')) return value + '回';
  if (key === 'occupancy' || key === 'usage' || key === 'uplift') return value + '%';
  return String(value);
}

function formatYen(n) { return '¥' + n.toLocaleString('ja-JP'); }
function formatNumber(n) { return n.toLocaleString('ja-JP'); }
function parseNumber(s) { return parseInt(s.replace(/[^0-9]/g, ''), 10) || 0; }

// ================================================================================
// コスト入力処理
// ================================================================================
function onCostInput(el) {
  const raw = el.value.replace(/[^0-9]/g, '');
  const num = parseInt(raw, 10) || 0;
  const pos = el.selectionStart;
  const oldLen = el.value.length;
  el.value = formatNumber(num);
  const newLen = el.value.length;
  el.setSelectionRange(pos + (newLen - oldLen), pos + (newLen - oldLen));
}

// ================================================================================
// ランニングコスト自動計算（業種別ロジック）
// ================================================================================
function calculateRunningCost() {
  const isAutoMode = document.getElementById('running-mode-toggle')?.checked;
  if (!isAutoMode) return;

  const units = Number(document.getElementById('slider-units')?.value || 1);
  const fixedCostPerUnit = getFixedCostPerUnit();

  let electricityCost = 0;
  let fixedCost = 0;
  let electricityDetail = '';

  if (currentModel === 'home_consumer') {
    // 家庭用: 週の頻度から年間使用回数を計算
    const usage = Number(document.getElementById('slider-usage')?.value || 3);
    const annualUsage = usage * 52;
    // 2kW × 2時間 × 30円/kWh = 120円/回
    electricityCost = annualUsage * 2 * 2 * 30;
    fixedCost = parseNumber(document.getElementById('fixed-cost-input')?.value || '15,000');
    electricityDetail = `120円/回 × ${annualUsage}回/年`;

  } else if (currentModel === 'housing_rental') {
    // 賃貸マンション: 電気代は入居者負担のためゼロ、固定費(メンテナンス)のみ
    electricityCost = 0;
    fixedCost = fixedCostPerUnit * units;
    electricityDetail = '入居者負担のため計上なし';

  } else if (currentModel === 'health_spa') {
    // 温浴施設: 収容人数1人あたり月13,000円
    const monthlyCost = spaCapacity * 13000;
    electricityCost = monthlyCost * 12;
    fixedCost = fixedCostPerUnit; // 1施設あたり
    electricityDetail = `${spaCapacity}人 × ¥13,000/月 × 12ヶ月` +
      `<br><span style="font-size:10px;color:#7a8a9e;line-height:1.3;display:inline-block;margin-top:4px;">` +
      `※ 1人あたり1.5kW × 単価27円/kWh × 18時間/日 × 60%（温度維持の平均消費電力）× 30日 ≒ 13,000円/月で算出</span>`;

  } else {
    // 旅館・ホテル: 台数 × 稼働率ベース（利用がない日は電源を入れない前提）
    const occupancy = Number(document.getElementById('slider-occupancy')?.value || 60);
    const saunaCount = units;
    const annualOperatingDays = Math.round(365 * (occupancy / 100));
    // 事業用: 3kW × 2時間 × 30円/kWh = 180円/回/台
    electricityCost = annualOperatingDays * saunaCount * 3 * 2 * 30;
    fixedCost = fixedCostPerUnit * saunaCount;
    const monthlyPerUnit = Math.round(electricityCost / saunaCount / 12);
    electricityDetail = `約${monthlyPerUnit.toLocaleString()}円/月/台 × ${saunaCount}台 × 12ヶ月`;
  }

  const totalRunningCost = Math.round(electricityCost + fixedCost);
  const runningInput = document.getElementById('input-running');
  if (runningInput) runningInput.value = formatNumber(totalRunningCost);

  updateCostBreakdown(fixedCost, electricityCost, totalRunningCost, electricityDetail);
}

function getFixedCostPerUnit() {
  const fixedCostInput = document.getElementById('fixed-cost-input');
  const customFixedCost = fixedCostInput ? parseNumber(fixedCostInput.value) : null;
  if (customFixedCost) return customFixedCost;
  if (currentModel === 'hotel_ryokan') return 10000;
  if (currentModel === 'housing_rental') return 15000;
  if (currentModel === 'health_spa') return 100000;
  return 10000;
}

function updateCostBreakdown(fixed, electricity, total, electricityDetail) {
  const breakdownEl = document.getElementById('cost-breakdown');
  if (!breakdownEl) return;

  breakdownEl.innerHTML = `
    <div class="cost-item"><span>固定費(メンテナンス等):</span><strong>${formatYen(Math.round(fixed))}</strong></div>
    <div class="cost-item"><span>変動費(電気代):</span><strong>${formatYen(Math.round(electricity))}</strong></div>
    <div class="cost-detail">└ ${electricityDetail}</div>
    <div class="cost-total"><span>合計:</span><strong>${formatYen(total)}</strong></div>
  `;
}

// ================================================================================
// 計算メイン
// ================================================================================
function calculate() {
  const cfg = modelConfigs[currentModel];

  const p = {
    price: Number(document.getElementById('slider-price').value),
    occupancy: Number(document.getElementById('slider-occupancy').value),
    usage: Number(document.getElementById('slider-usage').value),
    units: Number(document.getElementById('slider-units').value),
    uplift: Number(document.getElementById('slider-uplift').value),
  };
  const initialCost = parseNumber(document.getElementById('input-cost').value);

  calculateRunningCost();
  const runningCost = parseNumber(document.getElementById('input-running').value);

  const lifespanInput = document.getElementById('input-lifespan');
  const LIFESPAN = lifespanInput ? Number(lifespanInput.value) : 10;
  const targetPaybackInput = document.getElementById('input-target-payback');
  const targetPaybackYears = targetPaybackInput ? Number(targetPaybackInput.value) : 5;
  const isConsumer = currentModel === 'home_consumer';

  // スライダーラベル更新
  document.getElementById('value-price').textContent = formatYen(p.price);

  let occupancyUnit = '%';
  if (cfg.labels.occupancy.includes('交通費')) {
    document.getElementById('value-occupancy').textContent = formatYen(p.occupancy);
  } else if (cfg.labels.occupancy.includes('来場者')) {
    document.getElementById('value-occupancy').textContent = p.occupancy + '人';
  } else {
    document.getElementById('value-occupancy').textContent = p.occupancy + '%';
  }

  if (cfg.labels.usage.includes('週の頻度')) {
    document.getElementById('value-usage').textContent = p.usage + '回';
  } else {
    document.getElementById('value-usage').textContent = p.usage + '%';
  }

  document.getElementById('value-units').textContent = p.units + ' ' + cfg.unitSuffix;
  document.getElementById('value-uplift').textContent = p.uplift + '%';

  // 単価アップ金額を表示
  const upliftAmountEl = document.getElementById('uplift-amount-display');
  if (upliftAmountEl) {
    const upliftAmount = Math.round(p.price * (p.uplift / 100));
    upliftAmountEl.textContent = upliftAmount > 0
      ? formatYen(p.price) + ' × ' + p.uplift + '% = 1人あたり ' + formatYen(upliftAmount) + ' アップ'
      : '';
  }

  // 計算タイプ分岐
  let revenueIncrease, annualProfit, roi, paybackYears;

  if (currentCalculationType === 'budget_reverse') {
    revenueIncrease = cfg.calc(p);
    annualProfit = revenueIncrease - runningCost;
    const maxInitialCost = annualProfit * targetPaybackYears;
    displayBudgetReverseResults(maxInitialCost, annualProfit, targetPaybackYears);
    return;
  } else if (currentCalculationType === 'uplift_reverse') {
    const requiredAnnualProfit = initialCost / targetPaybackYears;
    const requiredRevenue = requiredAnnualProfit + runningCost;
    const baseRevenue = cfg.calc({...p, uplift: 1});
    const requiredUplift = (requiredRevenue / baseRevenue) * 1;
    displayUpliftReverseResults(requiredUplift, requiredRevenue, targetPaybackYears);
    return;
  } else {
    revenueIncrease = cfg.calc(p);
    annualProfit = revenueIncrease - runningCost;
    roi = initialCost > 0 ? (annualProfit / initialCost) * 100 : 0;
    paybackYears = annualProfit > 0 ? initialCost / annualProfit : Infinity;
  }

  // ROIゲージ
  const roiSection = document.querySelector('.roi-kpi-section:has(#gauge-arc)');
  if (roiSection) roiSection.style.display = isConsumer ? 'none' : 'block';

  if (!isConsumer) {
    const gaugeMax = getGaugeMax(roi);
    const roiClamped = Math.max(0, Math.min(gaugeMax, roi));
    const arcLength = 283;
    const dashLength = (roiClamped / gaugeMax) * arcLength;
    const gaugeArc = document.getElementById('gauge-arc');
    gaugeArc.setAttribute('stroke-dasharray', dashLength + ' ' + arcLength);
    updateGaugeTicks(gaugeMax);

    const roiEl = document.getElementById('roi-value');
    roiEl.textContent = roi.toFixed(1);

    let colorClass = 'bad', strokeColor = '#d94a4a';
    if (roi >= 20) { colorClass = 'excellent'; strokeColor = '#4a90d9'; }
    else if (roi >= 10) { colorClass = 'good'; strokeColor = '#3daa5c'; }
    else if (roi >= 0) { colorClass = 'warning'; strokeColor = '#e8871e'; }

    roiEl.className = 'gauge-value ' + colorClass;
    gaugeArc.setAttribute('stroke', strokeColor);
  }

  // 回収期間
  const paybackEl = document.getElementById('payback-years');
  const paybackMonthsEl = document.getElementById('payback-months');
  const paybackBarEl = document.getElementById('payback-bar-fill');
  const warningEl = document.getElementById('negative-warning');
  const paybackMonthsNumEl = document.getElementById('payback-months-num');

  if (annualProfit <= 0) {
    paybackEl.textContent = '∞';
    if (paybackMonthsNumEl) paybackMonthsNumEl.style.display = 'none';
    document.querySelectorAll('.roi-payback-unit').forEach(u => u.style.display = 'none');
    paybackMonthsEl.textContent = '回収不可';
    paybackBarEl.style.width = '100%';
    paybackBarEl.style.background = 'linear-gradient(90deg, #d94a4a, #e88)';
    warningEl.classList.add('show');
    if (isConsumer) {
      warningEl.innerHTML = '⚠ <strong>サウナ施設に通ったほうがコスパが良いです。</strong>';
    }
  } else {
    const years = paybackYears;
    const totalMonths = Math.round(years * 12);
    const dispYears = Math.floor(years);
    const dispMonths = totalMonths - dispYears * 12;

    paybackEl.textContent = dispYears;
    if (paybackMonthsNumEl) {
      paybackMonthsNumEl.textContent = dispMonths;
      paybackMonthsNumEl.style.display = 'inline';
    }
    document.querySelectorAll('.roi-payback-unit').forEach(u => u.style.display = 'inline');
    paybackMonthsEl.textContent = '約' + totalMonths + 'ヶ月（' + years.toFixed(2) + '年）';

    const barPct = Math.min(100, Math.max(5, (1 - years / LIFESPAN) * 100));
    paybackBarEl.style.width = barPct + '%';
    paybackBarEl.style.background = years <= 3 ? 'linear-gradient(90deg, #3daa5c, #5bc47a)' :
                                     years <= 5 ? 'linear-gradient(90deg, #e8871e, #f5a623)' :
                                     'linear-gradient(90deg, #d94a4a, #e88)';

    if (isConsumer && years > LIFESPAN) {
      warningEl.innerHTML = '⚠ <strong>投資回収期間が想定耐用年数(' + LIFESPAN + '年)を超えています。サウナ施設に通ったほうがコスパが良いです。</strong>';
      warningEl.classList.add('show');
    } else {
      warningEl.classList.remove('show');
    }
  }

  // 利益チャート
  const profitSection = document.querySelector('.roi-kpi-section:has(#chart-container)');
  if (profitSection) profitSection.style.display = isConsumer ? 'none' : 'block';

  if (!isConsumer) {
    const profitEl = document.getElementById('profit-amount');
    const arrowEl = document.getElementById('profit-arrow');
    profitEl.textContent = formatYen(Math.round(annualProfit));
    if (annualProfit >= 0) {
      profitEl.style.color = '#3daa5c'; arrowEl.textContent = '↑'; arrowEl.style.color = '#3daa5c';
    } else {
      profitEl.style.color = '#d94a4a'; arrowEl.textContent = '↓'; arrowEl.style.color = '#d94a4a';
    }
    renderChart(revenueIncrease, runningCost, annualProfit);

    // 累積純利益サマリー
    updateCumulativeSummary(annualProfit, initialCost, LIFESPAN);
  } else {
    const summaryEl = document.getElementById('cumulative-summary');
    if (summaryEl) summaryEl.innerHTML = '';
  }

  // 計算式・フッター更新
  updateFormulaSection(p, revenueIncrease, runningCost, initialCost, roi, paybackYears);
  updateFooter(LIFESPAN);
  updateSliderFills();
}

// ================================================================================
// 累積純利益サマリー
// ================================================================================
function updateCumulativeSummary(annualProfit, initialCost, lifespan) {
  const summaryEl = document.getElementById('cumulative-summary');
  if (!summaryEl) return;

  if (annualProfit <= 0) {
    summaryEl.innerHTML = '';
    return;
  }

  const cumulativeProfit = annualProfit * lifespan - initialCost;
  const cumulativeFormatted = formatYen(Math.round(cumulativeProfit));

  if (cumulativeProfit > 0) {
    summaryEl.innerHTML =
      `耐用年数<strong>${lifespan}年</strong>での累積純利益（初期費用差引後）:<br>` +
      `<span class="cumulative-highlight">${cumulativeFormatted}</span>`;
  } else {
    summaryEl.innerHTML =
      `耐用年数<strong>${lifespan}年</strong>では初期費用を回収しきれません。<br>` +
      `累積損失: <span class="cumulative-highlight" style="color:#d94a4a">${cumulativeFormatted}</span>`;
  }
}

// ================================================================================
// フッター動的更新
// ================================================================================
function updateFooter(lifespan) {
  const footer = document.getElementById('roi-footer');
  if (!footer) return;
  footer.innerHTML = '※ 本シミュレーションは概算です。実際の導入効果は施設の条件により異なります。<br>' +
    '<strong>【計算条件】</strong> 想定耐用年数: ' + lifespan + '年 | 計算基準: 年間ベース';
}

// ================================================================================
// ゲージ関連
// ================================================================================
function getGaugeMax(roi) {
  if (roi <= 50) return 50;
  if (roi <= 100) return 100;
  if (roi <= 200) return 200;
  if (roi <= 500) return 500;
  return Math.ceil(roi / 100) * 100;
}

function updateGaugeTicks(gaugeMax) {
  const ticksEl = document.getElementById('gauge-ticks');
  if (!ticksEl) return;

  let ticks = [];
  if (gaugeMax <= 50) ticks = [0, 10, 20, 30, 50];
  else if (gaugeMax <= 100) ticks = [0, 25, 50, 75, 100];
  else if (gaugeMax <= 200) ticks = [0, 50, 100, 150, 200];
  else if (gaugeMax <= 500) ticks = [0, 100, 200, 300, 500];
  else {
    const step = Math.ceil(gaugeMax / 4 / 100) * 100;
    ticks = [0];
    for (let i = 1; i <= 3; i++) ticks.push(step * i);
    ticks.push(gaugeMax);
  }

  const cx = 110, cy = 110, r = 90;
  let html = '';
  ticks.forEach(val => {
    const ratio = val / gaugeMax;
    const angle = Math.PI * (1 - ratio);
    const ox = cx + (r + 14) * Math.cos(angle);
    const oy = cy - (r + 14) * Math.sin(angle);
    html += `<text x="${ox.toFixed(1)}" y="${oy.toFixed(1)}" font-size="9" fill="#7a8a9e" font-family="DM Sans" text-anchor="middle" dominant-baseline="middle">${val}%</text>`;
  });
  ticksEl.innerHTML = html;
}

// ================================================================================
// 計算式セクション（業種別期間表現対応 — E2修正）
// ================================================================================
function updateFormulaSection(p, revenueIncrease, runningCost, initialCost, roi, paybackYears) {
  const cfg = modelConfigs[currentModel];
  const isConsumer = currentModel === 'home_consumer';

  const formulaBody1 = document.getElementById('formula-body-1');
  const formulaBody2 = document.getElementById('formula-body-2');
  const formulaBody3 = document.getElementById('formula-body-3');
  const formulaTitle1 = document.getElementById('formula-title-1');
  if (!formulaBody1) return;

  if (isConsumer) {
    if (formulaTitle1) formulaTitle1.textContent = '① 年間施設コストの計算';
    formulaBody1.innerHTML =
      '(施設代/回 + 交通費/回) × 週の頻度 × 52週<br><br>' +
      '<strong>現在の入力値:</strong><br>' +
      formatYen(p.price) + ' + ' + formatYen(p.occupancy) + ' = ' + formatYen(p.price + p.occupancy) + '/回<br>' +
      formatYen(p.price + p.occupancy) + ' × ' + p.usage + '回 × 52週<br>' +
      '= <strong>' + formatYen(Math.round(revenueIncrease)) + '</strong>';
  } else {
    if (formulaTitle1) formulaTitle1.textContent = '① 年間売上増加額の計算';
    const labels = cfg.labels;
    // 業種ごとの期間表現
    if (currentModel === 'hotel_ryokan') {
      // 旅館・ホテル: 単価アップ額 × 稼働率 × 客室数 × 365日
      formulaBody1.innerHTML =
        labels.price + ' × ' + labels.uplift + ' × ' + labels.occupancy + ' × ' + labels.units + ' × 365日<br><br>' +
        '<strong>現在の入力値:</strong><br>' +
        formatYen(p.price) + ' × ' + p.uplift + '% × ' + p.occupancy + '% × ' + p.units + cfg.unitSuffix + ' × 365日<br>' +
        '= <strong>' + formatYen(Math.round(revenueIncrease)) + '</strong>';
    } else if (currentModel === 'housing_rental') {
      // 賃貸マンション: 単価アップ額 × 入居率 × 戸数 × 12ヶ月
      formulaBody1.innerHTML =
        labels.price + ' × ' + labels.uplift + ' × ' + labels.occupancy + ' × ' + labels.units + ' × 12ヶ月<br><br>' +
        '<strong>現在の入力値:</strong><br>' +
        formatYen(p.price) + ' × ' + p.uplift + '% × ' + p.occupancy + '% × ' + p.units + cfg.unitSuffix + ' × 12ヶ月<br>' +
        '= <strong>' + formatYen(Math.round(revenueIncrease)) + '</strong>';
    } else if (currentModel === 'health_spa') {
      // 温浴施設: (客単価 × アップ率 - 入湯税) × 来場者数 × 営業日数
      const bathingTax = parseNumber(document.getElementById('input-bathing-tax')?.value || '150');
      const perVisitor = p.price * (p.uplift / 100) - bathingTax;
      formulaBody1.innerHTML =
        '(' + labels.price + ' × ' + labels.uplift + ' - 入湯税) × ' + labels.occupancy + ' × ' + labels.units + '<br><br>' +
        '<strong>現在の入力値:</strong><br>' +
        '(' + formatYen(p.price) + ' × ' + p.uplift + '% - ' + formatYen(bathingTax) + ') × ' + p.occupancy + '人 × ' + p.units + cfg.unitSuffix + '<br>' +
        '= (' + formatYen(Math.round(p.price * p.uplift / 100)) + ' - ' + formatYen(bathingTax) + ') × ' + p.occupancy + '人 × ' + p.units + cfg.unitSuffix + '<br>' +
        '= ' + formatYen(Math.max(0, Math.round(perVisitor))) + '/人 × ' + (p.occupancy * p.units).toLocaleString() + '人/年<br>' +
        '= <strong>' + formatYen(Math.round(revenueIncrease)) + '</strong>';
    }
  }

  if (formulaBody2) {
    formulaBody2.innerHTML =
      '(年間売上増加額 - 年間ランニングコスト) ÷ 初期投資 × 100<br><br>' +
      '<strong>現在の入力値:</strong><br>' +
      '(' + formatYen(Math.round(revenueIncrease)) + ' - ' + formatYen(runningCost) + ') ÷ ' + formatYen(initialCost) + ' × 100<br>' +
      '= <strong>' + (initialCost > 0 ? roi.toFixed(1) : '—') + '%</strong>';
  }

  if (formulaBody3) {
    const ap = revenueIncrease - runningCost;
    let paybackStr;
    if (ap <= 0) {
      paybackStr = '<strong>回収不可</strong>（年間利益がマイナス）';
    } else {
      const totalMonths = Math.round(paybackYears * 12);
      const dy = Math.floor(paybackYears);
      const dm = totalMonths - dy * 12;
      paybackStr = '<strong>' + dy + '年' + dm + 'ヶ月</strong>（約' + totalMonths + 'ヶ月）';
    }
    formulaBody3.innerHTML =
      '初期投資 ÷ (年間売上増加額 - 年間ランニングコスト)<br><br>' +
      '<strong>現在の入力値:</strong><br>' +
      formatYen(initialCost) + ' ÷ (' + formatYen(Math.round(revenueIncrease)) + ' - ' + formatYen(runningCost) + ')<br>' +
      '= ' + paybackStr;
  }
}

// ================================================================================
// アコーディオン / 計算式折りたたみ
// ================================================================================
function toggleInputSection(headerEl) {
  const body = headerEl.nextElementSibling;
  if (body) body.classList.toggle('open');
  headerEl.classList.toggle('collapsed');
}

function toggleFormulaSection() {
  const content = document.getElementById('formula-content');
  const icon = document.getElementById('formula-toggle-icon');
  if (content) content.classList.toggle('open');
  if (icon) icon.classList.toggle('open');
}

// ================================================================================
// チャート描画
// ================================================================================
function renderChart(revenue, cost, profit) {
  const container = document.getElementById('chart-container');
  const maxVal = Math.max(revenue, cost, Math.abs(profit), 1);
  const maxH = 140;

  const bars = [
    { label: '売上増加', value: revenue, cls: 'revenue' },
    { label: 'コスト', value: cost, cls: 'cost' },
    { label: '純利益', value: Math.abs(profit), cls: profit >= 0 ? 'profit' : 'cost' },
  ];

  container.innerHTML = bars.map(b => {
    const h = Math.max(4, (b.value / maxVal) * maxH);
    return `<div class="chart-bar-group">
      <div class="chart-bar ${b.cls}" style="height:${h}px"></div>
      <div class="chart-bar-label">${b.label}</div>
      <div class="chart-bar-label" style="font-weight:600;color:#333">${formatYen(Math.round(b.value))}</div>
    </div>`;
  }).join('');
}

// ================================================================================
// 逆算モード表示
// ================================================================================
function displayBudgetReverseResults(maxInitialCost, annualProfit, targetYears) {
  const roiSection = document.querySelector('.roi-kpi-section:has(#gauge-arc)');
  if (roiSection) roiSection.style.display = 'none';

  document.querySelector('.roi-kpi-section:has(#payback-years) .roi-kpi-title').textContent = 'かけられる初期費用の上限';
  const paybackEl = document.getElementById('payback-years');
  const paybackMonthsEl = document.getElementById('payback-months');
  const paybackBarEl = document.getElementById('payback-bar-fill');
  const warningEl = document.getElementById('negative-warning');

  const paybackMonthsNumEl = document.getElementById('payback-months-num');
  if (paybackMonthsNumEl) paybackMonthsNumEl.style.display = 'none';
  document.querySelectorAll('.roi-payback-unit').forEach(u => u.style.display = 'none');

  paybackEl.textContent = formatYen(Math.round(maxInitialCost));
  paybackEl.style.fontSize = '32px';
  paybackMonthsEl.textContent = `(目標回収期間: ${targetYears}年)`;

  if (maxInitialCost <= 0) {
    paybackBarEl.style.width = '0%';
    paybackBarEl.style.background = 'linear-gradient(90deg, #d94a4a, #e88)';
    warningEl.innerHTML = '⚠ <strong>年間利益がマイナスのため、初期費用をかけることができません。</strong>';
    warningEl.classList.add('show');
  } else {
    const barPct = Math.min(100, Math.max(5, (maxInitialCost / 10000000) * 100));
    paybackBarEl.style.width = barPct + '%';
    paybackBarEl.style.background = 'linear-gradient(90deg, #3daa5c, #5bc47a)';
    warningEl.classList.remove('show');
  }

  const profitSection = document.querySelector('.roi-kpi-section:has(#chart-container)');
  if (profitSection) profitSection.style.display = 'none';

  const summaryEl = document.getElementById('cumulative-summary');
  if (summaryEl) summaryEl.innerHTML = '';

  updateSliderFills();
}

function displayUpliftReverseResults(requiredUplift, requiredRevenue, targetYears) {
  const roiSection = document.querySelector('.roi-kpi-section:has(#gauge-arc)');
  if (roiSection) roiSection.style.display = 'none';

  document.querySelector('.roi-kpi-section:has(#payback-years) .roi-kpi-title').textContent = '必要な単価アップ率';
  const paybackEl = document.getElementById('payback-years');
  const paybackMonthsEl = document.getElementById('payback-months');
  const paybackBarEl = document.getElementById('payback-bar-fill');
  const warningEl = document.getElementById('negative-warning');

  const paybackMonthsNumEl = document.getElementById('payback-months-num');
  if (paybackMonthsNumEl) paybackMonthsNumEl.style.display = 'none';
  const units = document.querySelectorAll('.roi-payback-unit');
  units.forEach((u, i) => {
    if (i === 0) { u.textContent = '%'; u.style.display = 'inline'; }
    else u.style.display = 'none';
  });

  paybackEl.textContent = requiredUplift.toFixed(1);
  paybackEl.style.fontSize = '48px';
  paybackMonthsEl.textContent = `(目標回収期間: ${targetYears}年)`;

  if (requiredUplift <= 0 || requiredUplift > 100) {
    paybackBarEl.style.width = '100%';
    paybackBarEl.style.background = 'linear-gradient(90deg, #d94a4a, #e88)';
    warningEl.innerHTML = '⚠ <strong>現実的な単価アップ率では回収できません。初期費用またはランニングコストを見直してください。</strong>';
    warningEl.classList.add('show');
  } else {
    const barPct = Math.min(100, Math.max(5, (1 - requiredUplift / 50) * 100));
    paybackBarEl.style.width = barPct + '%';
    paybackBarEl.style.background = requiredUplift <= 15 ? 'linear-gradient(90deg, #3daa5c, #5bc47a)' :
                                     requiredUplift <= 30 ? 'linear-gradient(90deg, #e8871e, #f5a623)' :
                                     'linear-gradient(90deg, #d94a4a, #e88)';
    warningEl.classList.remove('show');
  }

  const profitSection = document.querySelector('.roi-kpi-section:has(#chart-container)');
  if (profitSection) profitSection.style.display = 'none';

  const summaryEl = document.getElementById('cumulative-summary');
  if (summaryEl) summaryEl.innerHTML = '';

  updateSliderFills();
}

// ================================================================================
// スライダー塗り更新
// ================================================================================
function updateSliderFills() {
  document.querySelectorAll('input[type="range"]').forEach(slider => {
    const pct = ((slider.value - slider.min) / (slider.max - slider.min)) * 100;
    slider.style.background = `linear-gradient(to right, #e8871e 0%, #e8871e ${pct}%, #e2e8f0 ${pct}%, #e2e8f0 100%)`;
  });
}

// ================================================================================
// ランニングコストモード切替
// ================================================================================
function onRunningModeChange() {
  const isAuto = document.getElementById('running-mode-toggle').checked;
  const autoMode = document.getElementById('running-auto-mode');
  const manualMode = document.getElementById('running-manual-mode');
  if (autoMode) autoMode.style.display = isAuto ? 'block' : 'none';
  if (manualMode) manualMode.style.display = isAuto ? 'none' : 'block';
  calculate();
}

// ================================================================================
// URL状態共有（D3）
// ================================================================================
function shareCurrentState() {
  const params = new URLSearchParams();
  params.set('model', currentModel);
  params.set('calc', currentCalculationType);
  params.set('price', document.getElementById('slider-price').value);
  params.set('occ', document.getElementById('slider-occupancy').value);
  params.set('usage', document.getElementById('slider-usage').value);
  params.set('units', document.getElementById('slider-units').value);
  params.set('uplift', document.getElementById('slider-uplift').value);
  params.set('cost', parseNumber(document.getElementById('input-cost').value));
  params.set('life', document.getElementById('input-lifespan')?.value || '10');

  const url = window.location.origin + window.location.pathname + '?' + params.toString();

  if (navigator.clipboard) {
    navigator.clipboard.writeText(url).then(() => {
      alert('URLをクリップボードにコピーしました。\nこのURLを共有すると、同じ設定でシミュレーションを再現できます。');
    }).catch(() => {
      prompt('以下のURLをコピーしてください:', url);
    });
  } else {
    prompt('以下のURLをコピーしてください:', url);
  }
}

function restoreFromURL() {
  const params = new URLSearchParams(window.location.search);
  if (!params.has('model')) return false;

  const model = params.get('model');
  if (modelConfigs[model]) {
    document.getElementById('industrySelect').value = model;
    currentModel = model;
    const cfg = modelConfigs[model];

    // onIndustryChange がスライダーをデフォルトに戻すので、先にモデル設定だけ適用
    onIndustryChange();

    // その後URLの値で上書き
    const setSlider = (id, key) => {
      const val = params.get(key);
      const el = document.getElementById(id);
      if (val && el) el.value = val;
    };

    setSlider('slider-price', 'price');
    setSlider('slider-occupancy', 'occ');
    setSlider('slider-usage', 'usage');
    setSlider('slider-units', 'units');
    setSlider('slider-uplift', 'uplift');

    if (params.has('cost')) {
      document.getElementById('input-cost').value = formatNumber(Number(params.get('cost')));
    }
    if (params.has('life')) {
      const lifeEl = document.getElementById('input-lifespan');
      if (lifeEl) lifeEl.value = params.get('life');
    }

    const calcType = params.get('calc');
    if (calcType) {
      currentCalculationType = calcType;
      const calcSelect = document.getElementById('calculationTypeSelect');
      if (calcSelect) calcSelect.value = calcType;
    }

    calculate();
    return true;
  }
  return false;
}

// ================================================================================
// 初期化
// ================================================================================
document.addEventListener('DOMContentLoaded', () => {
  // URLからの状態復元を試行、なければ通常初期化
  if (!restoreFromURL()) {
    updatePresetLabels();
    calculate();
  }
});
